import { ModuleNavigation } from '../ModuleNavigation';
import { AppUpdateNotice } from '../AppUpdateNotice';
import { roleLabel } from '@/design-system/roles';
import { useEffect, useState, type ReactNode } from 'react';
import { Avatar, Dialog, DialogContent, DialogTitle, Drawer, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, TextField, Tooltip } from '@mui/material';
import { SpaceDashboardOutlined as DashboardIcon, PointOfSaleOutlined as PointOfSaleIcon, StoreOutlined as StoreIcon, Inventory2Outlined as InventoryIcon, ShoppingCartOutlined as ShoppingCartIcon, AssessmentOutlined as AssessmentIcon, SettingsOutlined as SettingsIcon, BusinessOutlined as BusinessIcon, WarehouseOutlined as WarehouseIcon, PersonOutline as PersonIcon, PeopleOutline as PeopleIcon, LocalShippingOutlined as SupplierIcon, AdminPanelSettingsOutlined as AdminIcon, AccountBalanceOutlined as AccountingIcon, ShoppingBasketOutlined as PurchasingIcon, AssignmentReturnOutlined as ReturnsIcon, SearchOutlined, MenuOutlined, CloseOutlined, ContrastOutlined, ExpandMoreOutlined, LogoutOutlined } from '@mui/icons-material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts';
import { useUiStore } from '@/store';
import { LanguageSwitcher } from '@/components/ui';
import { SyncStatusIndicator, OfflineBanner } from '@/components/sync';
import { MockModeBanner } from '@/components/ui/MockModeBanner';
import { SubscriptionBanner } from '@/components/subscription';
import { useEntitlements, type FeatureKey } from '@/services/subscription';
import { hasAnyPermission, hasPermission, canAccessPathByPermissions } from '@/utils/roleAccess';
import Footer from '../Footer';

interface NavigationItem { textKey: string; icon: ReactNode; path: string; section: string; requiredPermissions: readonly string[]; requiredFeature?: FeatureKey; }
const MainLayout = ({ children }: { children: ReactNode }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [profile, setProfile] = useState<HTMLElement | null>(null);
  const [searchOpen, setSearchOpen] = useState(false); const [search, setSearch] = useState('');
  const navigate = useNavigate(); const location = useLocation();
  const { t, i18n } = useTranslation(); const { user, logout } = useAuth();
  const toggleTheme = useUiStore(state => state.toggleTheme);
  const { data: entitlements } = useEntitlements();
  useEffect(() => {
    const keydown = (event: KeyboardEvent) => { if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k' && !(event.target instanceof HTMLElement && event.target.closest('input,textarea,[contenteditable=true]'))) { event.preventDefault(); setSearchOpen(true); } };
    window.addEventListener('keydown', keydown); return () => window.removeEventListener('keydown', keydown);
  }, []);
  const allMenuItems: NavigationItem[] = [
    // Main Operations
    {
      textKey: "navigation.dashboard",
      icon: <DashboardIcon />,
      path: "/dashboard",
      section: "main",
      requiredPermissions: [],
    },
    {
      textKey: "navigation.pos",
      icon: <PointOfSaleIcon />,
      path: "/pos",
      section: "main",
      requiredPermissions: ["pos.create", "sales.create"],
    },

    // Products & Inventory
    {
      textKey: "navigation.products",
      icon: <StoreIcon />,
      path: "/products",
      section: "inventory",
      requiredPermissions: ["products.read"],
    },
    {
      textKey: "navigation.inventory",
      icon: <InventoryIcon />,
      path: "/inventory",
      section: "inventory",
      requiredPermissions: ["inventory.read"],
    },

    // Sales & Customers
    {
      textKey: "navigation.sales",
      icon: <ShoppingCartIcon />,
      path: "/sales",
      section: "sales",
      requiredPermissions: ["sales.read", "sales.create"],
    },
    {
      textKey: "navigation.customers",
      icon: <PersonIcon />,
      path: "/customers",
      section: "sales",
      requiredPermissions: ["customers.read"],
    },
    {
      textKey: "navigation.suppliers",
      icon: <SupplierIcon />,
      path: "/suppliers",
      section: "sales",
      requiredPermissions: [
        "purchasing.suppliers.read",
        "purchasing.suppliers.create",
      ],
    },
    {
      textKey: "navigation.purchasing",
      icon: <PurchasingIcon />,
      path: hasAnyPermission(user?.permissions, ["purchasing.orders.read", "purchasing.orders.create"]) ? "/purchasing/orders" : "/purchasing/invoices",
      section: "sales",
      requiredPermissions: [
        "purchasing.invoices.read",
        "purchasing.orders.read",
        "purchasing.orders.create",
      ],
    },
    {
      textKey: "navigation.returns",
      icon: <ReturnsIcon />,
      path: "/returns",
      section: "sales",
      requiredPermissions: ["returns.read", "returns.create"],
    },

    // Business Management
    {
      textKey: "navigation.branches",
      icon: <BusinessIcon />,
      path: "/branches",
      section: "management",
      requiredPermissions: ["branches.read"],
    },
    {
      textKey: "navigation.warehouses",
      icon: <WarehouseIcon />,
      path: "/warehouses",
      section: "management",
      requiredPermissions: ["warehouses.read", "inventory.read"],
    },

    // Reports & Accounting
    {
      textKey: "navigation.reports",
      icon: <AssessmentIcon />,
      path: "/reports",
      section: "reports",
      requiredPermissions: [
        "reporting.sales.read",
        "reporting.inventory.read",
        "reporting.financial.read",
        "reporting.dashboard.read",
      ],
      requiredFeature: "advanced_reports",
    },
    {
      textKey: "navigation.accounting",
      icon: <AccountingIcon />,
      path: hasPermission(user?.permissions, "accounting.gl_accounts.read") ? "/accounting" : hasPermission(user?.permissions, "accounting.journal_entries.read") ? "/accounting/journal-entries" : "/accounting/reports",
      section: "reports",
      requiredPermissions: [
        "accounting.journal_entries.read",
        "accounting.gl_accounts.read",
        "accounting.reports",
      ],
      requiredFeature: "accounting",
    },

    // Admin (admin only)
    {
      textKey: "navigation.admin",
      icon: <AdminIcon />,
      path: "/admin",
      section: "admin",
      requiredPermissions: ["audit.read", "settings.read"],
    },
    {
      textKey: "navigation.users",
      icon: <PeopleIcon />,
      path: "/users",
      section: "admin",
      requiredPermissions: ["users.read"],
    },

    // Settings
    {
      textKey: "navigation.settings",
      icon: <SettingsIcon />,
      path: "/settings",
      section: "system",
      requiredPermissions: ["settings.read", "settings.update"],
    },
  ];

  const menuItems = allMenuItems.filter(
    (item) =>
      (item.requiredPermissions.length === 0 ||
        hasAnyPermission(user?.permissions, item.requiredPermissions)) &&
      (!item.requiredFeature ||
        entitlements?.features[item.requiredFeature] !== false),
  );

  const sectionConfig = [
    { key: "main", label: t("designSystem.sections.main") },
    { key: "inventory", label: t("designSystem.sections.inventory") },
    { key: "sales", label: t("designSystem.sections.sales") },
    { key: "management", label: t("designSystem.sections.management") },
    { key: "reports", label: t("designSystem.sections.reports") },
    { key: "admin", label: t("designSystem.sections.admin") },
    { key: "system", label: t("designSystem.sections.system") },
  ];

  const sections = sectionConfig
    .map(({ key, label }) => ({
      key,
      label,
      items: menuItems.filter((item) => item.section === key),
    }))
    .filter((section) => section.items.length > 0);


  const go = (path: string) => { setDrawerOpen(false); setSearchOpen(false); setProfile(null); setSearch(''); navigate(path); };
  const quickActions = [
    {path:'/products/new',textKey:'designSystem.newProduct',icon:<StoreIcon />,permission:'products.create'},
    {path:'/customers/new',textKey:'designSystem.newCustomer',icon:<PersonIcon />,permission:'customers.create'},
    {path:'/sales/invoices/new',textKey:'designSystem.newInvoice',icon:<ShoppingCartIcon />,permission:'sales.create'},
    {path:'/purchasing/orders/new',textKey:'designSystem.newOrder',icon:<PurchasingIcon />,permission:'purchasing.orders.create'},
  ].filter(item => hasPermission(user?.permissions,item.permission) && canAccessPathByPermissions(item.path,user?.permissions));
  const destinations = [...menuItems,...quickActions].filter(item => t(item.textKey).toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()));
  const sidebar = <>
    <Link className="app-brand" to="/dashboard"><span className="brand-mark">ز</span><div><strong>{t('app.name')}</strong><small>{user?.tenantSlug ?? t('designSystem.workspace')}</small></div></Link>
    <button className="sidebar-profile" aria-label={t('designSystem.accountMenu')} aria-haspopup="menu" aria-expanded={Boolean(profile)} onClick={event => setProfile(event.currentTarget)}><Avatar>{user?.username?.slice(0,1)}</Avatar><div><strong>{user?.username}</strong><small>{roleLabel(user?.role ?? '', i18n.language)}</small></div><ExpandMoreOutlined /></button>
    <nav className="sidebar-nav" aria-label={t('designSystem.navigation')}>{sections.map(section => <section className="nav-section" key={section.key}><p>{section.label}</p>{section.items.map(item => {
      const active = location.pathname === item.path || location.pathname.startsWith(item.path+'/');
      return <Tooltip title={t(item.textKey)} placement={i18n.dir() === 'rtl' ? 'left' : 'right'} key={item.path}><Link to={item.path} className={active ? 'active' : ''} aria-current={active ? 'page' : undefined} onClick={() => setDrawerOpen(false)}><span className="nav-icon">{item.icon}</span><span className="nav-label">{t(item.textKey)}</span></Link></Tooltip>;
    })}</section>)}</nav>
    <div className="sidebar-footer"><Tooltip title={t('designSystem.toggleTheme')}><IconButton aria-label={t('designSystem.toggleTheme')} onClick={toggleTheme}><ContrastOutlined /></IconButton></Tooltip><LanguageSwitcher variant="icon" /><SyncStatusIndicator compact /></div>
  </>;
  return <div className="app-shell">
    <aside className="app-sidebar">{sidebar}</aside>
    <Drawer anchor={i18n.dir() === 'rtl' ? 'right' : 'left'} open={drawerOpen} onClose={() => setDrawerOpen(false)} slotProps={{ paper: { sx: { width: 'min(320px,90vw)', p: 2 } } }}><IconButton aria-label={t('designSystem.closeMenu')} onClick={() => setDrawerOpen(false)}><CloseOutlined /></IconButton>{sidebar}</Drawer>
    <div className="workspace"><header className="app-topbar"><IconButton className="mobile-menu-button" aria-label={t('designSystem.openMenu')} onClick={() => setDrawerOpen(true)}><MenuOutlined /></IconButton><button className="quick-search" onClick={() => setSearchOpen(true)}><SearchOutlined /><span>{t('designSystem.searchPages')}</span><kbd dir="ltr">Ctrl K</kbd></button><div className="top-actions"><SyncStatusIndicator compact /><IconButton aria-label={t('designSystem.toggleTheme')} onClick={toggleTheme}><ContrastOutlined /></IconButton><IconButton aria-label={t('designSystem.accountMenu')} onClick={event => setProfile(event.currentTarget)}><Avatar sx={{width:32,height:32}}>{user?.username?.slice(0,1)}</Avatar></IconButton></div></header><main className="page-content"><MockModeBanner /><OfflineBanner /><SubscriptionBanner /><AppUpdateNotice /><ModuleNavigation />{children}</main><Footer compact /></div>
    <Menu anchorEl={profile} open={Boolean(profile)} onClose={() => setProfile(null)}>{hasAnyPermission(user?.permissions,['settings.read','settings.update']) && <MenuItem onClick={() => go('/settings')}><ListItemIcon><SettingsIcon /></ListItemIcon>{t('navigation.settings')}</MenuItem>}<MenuItem onClick={() => { setProfile(null); void logout(); }}><ListItemIcon><LogoutOutlined /></ListItemIcon>{t('auth.logout')}</MenuItem></Menu>
    <Dialog open={searchOpen} onClose={() => setSearchOpen(false)} dir={i18n.dir()} aria-labelledby="customer-command-title"><DialogTitle id="customer-command-title">{t('designSystem.quickNavigation')}</DialogTitle><DialogContent><TextField autoFocus fullWidth label={t('designSystem.searchPages')} value={search} onChange={event => setSearch(event.target.value)} /><List>{destinations.map(item => <ListItemButton key={item.path} onClick={() => go(item.path)}><ListItemIcon>{item.icon}</ListItemIcon><ListItemText primary={t(item.textKey)} /></ListItemButton>)}</List>{!destinations.length && <p>{t('designSystem.noPages')}</p>}</DialogContent></Dialog>
  </div>;
};
export default MainLayout;
